import * as THREE from 'three';
import { FikoSahne } from '../../IFikoThreeJS/FikoSahne';

/**
 * ⚠️ CATIA-Style Surface Finish (Yüzey Kalitesi)
 * Surface roughness annotation with Ra value
 */

export class FikoYuzeyKalite extends THREE.Group {
    override type = 'FikoYuzeyKalite';

    // 🔴 SABİT BOYUT
    sabitBoyut: boolean = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegisti();

    private pozisyon: THREE.Vector3;
    private raValue: number;
    private islemTipi: string;
    private renk: string = '#FF9800';

    private sprite: THREE.Sprite | null = null;
    private canvas: HTMLCanvasElement;
    private context: CanvasRenderingContext2D;
    private texture: THREE.CanvasTexture;

    constructor(
        public sahne: FikoSahne,
        pozisyon: THREE.Vector3,
        raValue: number = 3.2,
        islemTipi: string = 'machining'
    ) {
        super();

        this.pozisyon = pozisyon.clone();
        this.raValue = raValue;
        this.islemTipi = islemTipi;

        this.canvas = document.createElement('canvas');
        this.canvas.width = 96;
        this.canvas.height = 64;
        const ctx = this.canvas.getContext('2d', { alpha: true });
        if (!ctx) throw new Error('Failed to get 2D context');
        this.context = ctx;
        this.texture = new THREE.CanvasTexture(this.canvas);

        this.name = 'Yüzey Kalitesi';
        this.renderOrder = 999;

        this.init();

        if (sahne) {
            this.hazirla(sahne);
        }
    }

    hazirla(sahne?: FikoSahne): void {
        if (!sahne || !sahne.anaGosterim || !sahne.anaGosterim.aktifKamera) return;
        this.sahne = sahne;

        try {
            if (this.sprite) {
                this.sprite.userData.originalScale = new THREE.Vector3().copy(this.sprite.scale);
                this.kameraYonDegisti();
            }
            if (!this.hazirlaListenerEklendi && sahne.anaGosterim.aktifKontrol) {
                sahne.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) { }
    }

    private kameraYonDegisti(): void {
        if (!this.sabitBoyut || !this.sahne?.anaGosterim?.aktifKamera || !this.sprite) return;
        const originalScale = this.sprite.userData.originalScale as THREE.Vector3;
        if (!originalScale) return;

        const kamera = this.sahne.anaGosterim.aktifKamera;
        const zoom = kamera?.zoom ?? 1;
        const factor = (kamera?.top - kamera?.bottom) / zoom;
        const scaleAdjustment = factor * 0.02;
        this.sprite.scale.set(originalScale.x * scaleAdjustment, originalScale.y * scaleAdjustment, 1);
        this.sprite.updateMatrix();
    }

    setSabitBoyut(aktif: boolean): void {
        this.sabitBoyut = aktif;
        if (aktif && this.sprite) {
            this.sprite.userData.originalScale = new THREE.Vector3().copy(this.sprite.scale);
            this.kameraYonDegisti();
        }
    }

    private init(): void {
        this.createSprite();
    }

    private createSprite(): void {
        const ctx = this.context;
        const width = 100;
        const height = 80;

        this.canvas.width = width;
        this.canvas.height = height;
        ctx.clearRect(0, 0, width, height);

        // Draw surface finish symbol (check mark shape)
        ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
        ctx.beginPath();
        ctx.moveTo(width / 2, 10);           // Top point
        ctx.lineTo(width - 10, height - 20); // Bottom right
        ctx.lineTo(10, height - 20);         // Bottom left
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = this.renk;
        ctx.lineWidth = 3;
        ctx.stroke();

        // Horizontal line at bottom
        ctx.beginPath();
        ctx.moveTo(10, height - 20);
        ctx.lineTo(width - 10, height - 20);
        ctx.stroke();

        // Ra value
        ctx.font = 'bold 16px Arial';
        ctx.fillStyle = this.renk;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`Ra ${this.raValue}`, width / 2, 30);

        // Process type (smaller text)
        ctx.font = 'bold 10px Arial';
        ctx.fillText(this.islemTipi.substring(0, 3).toUpperCase(), width / 2, 50);

        this.texture.needsUpdate = true;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: this.texture,
            transparent: true,
            depthTest: false
        });

        this.sprite = new THREE.Sprite(spriteMaterial);
        this.sprite.position.copy(this.pozisyon);
        this.sprite.scale.set(1.5, 1.2, 1);
        this.add(this.sprite);
    }

    dispose(): void {
        if (this.sahne?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.sahne.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }
        if (this.sprite) {
            this.sprite.material.map?.dispose();
            this.sprite.material.dispose();
        }
        this.texture.dispose();
    }
}
